function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function metricValues(hours, cost, sales) {
  return {
    hours,
    cost,
    sales,
    labor_percent: sales > 0 ? (cost / sales) * 100 : 0,
    splh: hours > 0 ? sales / hours : 0,
  };
}

function percentDelta(value, comparison) {
  return comparison ? ((value - comparison) / comparison) * 100 : null;
}

function metricDeltas(target, comparison) {
  return {
    hours_percent: percentDelta(target.hours, comparison.hours),
    cost_percent: percentDelta(target.cost, comparison.cost),
    labor_percent_points: target.labor_percent - comparison.labor_percent,
    splh_percent: percentDelta(target.splh, comparison.splh),
  };
}

function aggregateEvaluationPeriod(period) {
  const source = period?.dailyTestResults || period?.testResults || {};
  const company = {
    targetHours: 0,
    targetCost: 0,
    comparisonHours: 0,
    comparisonCost: 0,
    sales: 0,
  };
  const stores = [];

  Object.entries(source).forEach(([fallbackStoreName, rows]) => {
    const store = {
      storeId: String(rows?.[0]?.store_id || fallbackStoreName),
      storeName: rows?.[0]?.store_name || fallbackStoreName,
      targetHours: 0,
      targetCost: 0,
      comparisonHours: 0,
      comparisonCost: 0,
      sales: 0,
    };
    (rows || []).forEach((row) => {
      store.sales += numberValue(row.sales);
      (row.labor_results || []).forEach((labor) => {
        store.targetHours += numberValue(labor.new_hours);
        store.targetCost += numberValue(labor.new_cost);
        store.comparisonHours += numberValue(labor.old_hours);
        store.comparisonCost += numberValue(labor.old_cost);
      });
    });
    company.targetHours += store.targetHours;
    company.targetCost += store.targetCost;
    company.comparisonHours += store.comparisonHours;
    company.comparisonCost += store.comparisonCost;
    company.sales += store.sales;
    const target = metricValues(
      store.targetHours,
      store.targetCost,
      store.sales
    );
    const comparison = metricValues(
      store.comparisonHours,
      store.comparisonCost,
      store.sales
    );
    stores.push({
      store_id: store.storeId,
      store_name: store.storeName,
      target,
      comparison,
      deltas: metricDeltas(target, comparison),
    });
  });

  const target = metricValues(
    company.targetHours,
    company.targetCost,
    company.sales
  );
  const comparison = metricValues(
    company.comparisonHours,
    company.comparisonCost,
    company.sales
  );
  return {
    target,
    comparison,
    deltas: metricDeltas(target, comparison),
    stores,
  };
}

function aggregatePublishedEarned(
  laborPlanSummary,
  storeIds,
  dateStart,
  dateEnd
) {
  const selectedIds = new Set((storeIds || []).map(String));
  const storeSummaries = Object.fromEntries(
    (laborPlanSummary?.stores || []).map((store) => [
      String(store.store_id),
      store,
    ])
  );
  const company = { hours: 0, cost: 0, sales: 0 };
  const stores = {};

  Object.entries(laborPlanSummary?.store_series || {}).forEach(
    ([storeId, rows]) => {
      if (selectedIds.size > 0 && !selectedIds.has(String(storeId))) return;
      const summary = storeSummaries[String(storeId)];
      const values = {
        hours: 0,
        cost: numberValue(summary?.earned?.labor_cost),
        sales: 0,
      };
      (rows || []).forEach((row) => {
        if (
          row.period !== "historical" ||
          (dateStart && row.date < dateStart) ||
          (dateEnd && row.date > dateEnd)
        ) {
          return;
        }
        values.hours += numberValue(row.earned?.hours);
        values.sales += numberValue(row.actual?.sales);
      });
      stores[String(storeId)] = metricValues(
        values.hours,
        values.cost,
        values.sales
      );
      company.hours += values.hours;
      company.cost += values.cost;
      company.sales += values.sales;
    }
  );

  return {
    company: metricValues(company.hours, company.cost, company.sales),
    stores,
  };
}

function standardContributions(contributions, targetHours) {
  const components = { service: 0, support: 0, staffing: 0 };
  const standards = Object.values(contributions || {})
    .map((standard) => {
      const hours = numberValue(standard.hours);
      const component =
        standard.type === "routine"
          ? "service"
          : standard.type === "task"
            ? "support"
            : "staffing";
      components[component] += hours;
      return {
        name: standard.name,
        type: standard.type,
        role_type: standard.role_type,
        hours,
        percent_of_target_hours:
          targetHours > 0 ? (hours / targetHours) * 100 : 0,
      };
    })
    .sort((left, right) => right.hours - left.hours);
  return { components, standards };
}

function buildPeriodContext(
  period,
  comparisonLabel,
  laborPlanSummary,
  includePublishedEarned
) {
  if (!period) return null;
  const aggregate = aggregateEvaluationPeriod(period);
  const storeIds = aggregate.stores.map((store) => store.store_id);
  const publishedEarned = includePublishedEarned
    ? aggregatePublishedEarned(
        laborPlanSummary,
        storeIds,
        period.dateStart,
        period.dateEnd
      )
    : null;
  const breakdown = standardContributions(
    period.contributions,
    aggregate.target.hours
  );
  return {
    date_start: period.dateStart,
    date_end: period.dateEnd,
    comparison_label: comparisonLabel,
    target: aggregate.target,
    comparison: aggregate.comparison,
    published_earned: publishedEarned?.company || {},
    deltas: aggregate.deltas,
    components: breakdown.components,
    standard_contributions: breakdown.standards.slice(0, 300),
    store_count: aggregate.stores.length,
    stores: aggregate.stores.slice(0, 150).map((store) => {
      const storeBreakdown = standardContributions(
        period.contributionsByStore?.[store.store_name],
        store.target.hours
      );
      return {
        store_name: store.store_name,
        target: store.target,
        comparison: store.comparison,
        published_earned:
          publishedEarned?.stores?.[String(store.store_id)] || {},
        deltas: store.deltas,
        components: storeBreakdown.components,
        standard_contributions: storeBreakdown.standards.slice(0, 8),
      };
    }),
  };
}

export function getLaborModelEvaluationKey(target, publications = []) {
  if (!target?.uuid) return null;
  const publishedPlanKey =
    publications
      .map(
        (publication) =>
          `${publication.uuid}:${publication.effective_date || "legacy"}`
      )
      .sort()
      .join("|") || "published-plan";
  return [target.type, target.uuid, publishedPlanKey].join(":");
}

export function buildLaborModelAssistantEvaluationContext(
  evaluation,
  laborPlanSummary
) {
  if (!evaluation?.target?.uuid) {
    return null;
  }
  const isScenario = evaluation.target.type === "scenario";
  if (isScenario && !evaluation.target.revision) return null;
  const history = buildPeriodContext(
    evaluation.history,
    "Actual-used labor",
    laborPlanSummary,
    true
  );
  const forecast = buildPeriodContext(
    evaluation.forecast || evaluation,
    "Published labor plan",
    laborPlanSummary,
    false
  );
  return {
    target_type: evaluation.target.type,
    target_uuid: evaluation.target.uuid,
    scenario_revision: isScenario ? evaluation.target.revision : null,
    completed_at: evaluation.completedAt,
    store_count: Math.max(
      numberValue(history?.store_count),
      numberValue(forecast?.store_count)
    ),
    history,
    forecast,
  };
}
